import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import MonthlyMargin from "@/components/tms/MonthlyMargin";
import { ClipboardList, FileText, Truck, Mail, TrendingUp, PackageCheck, Clock, AlertTriangle, ArrowRight, ShoppingCart, Receipt, PiggyBank } from "lucide-react";

const eur = (v) => `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

const STATUS_CLS = {
    "En attente": "chip-attente",
    "En cours": "chip-cours",
    "Livré": "chip-livre",
    "Litige": "chip-litige",
};

export default function TmsDashboard() {
    const [stats, setStats] = useState(null);
    const [recentOt, setRecentOt] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, ot] = await Promise.all([
                    api.get("/stats"),
                    api.get("/ot"),
                ]);
                setStats(s.data);
                setRecentOt(ot.data.slice(0, 6));
            } catch {}
        })();
    }, []);

    const cards = [
        { label: "OT total", value: stats?.ot_total ?? "—", icon: ClipboardList, color: "text-sky-300", to: "/tms/ot" },
        { label: "Factures en attente", value: stats?.invoices_pending ?? "—", icon: FileText, color: "text-amber-300", to: "/tms/invoices" },
        { label: "CA encaissé (TTC)", value: stats ? `${stats.revenue_paid_ttc.toLocaleString("fr-FR")} €` : "—", icon: TrendingUp, color: "text-emerald-300" },
        { label: "Transporteurs partenaires", value: stats?.carriers_count ?? "—", icon: Truck, color: "text-orange-300", to: "/tms/carriers" },
    ];

    const statusTiles = [
        { k: "En attente", icon: Clock, cls: "chip-attente" },
        { k: "En cours", icon: Truck, cls: "chip-cours" },
        { k: "Livré", icon: PackageCheck, cls: "chip-livre" },
        { k: "Litige", icon: AlertTriangle, cls: "chip-litige" },
    ];

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-7xl mx-auto">
            <header className="flex flex-wrap items-end justify-between gap-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">TMS Interne</div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Tableau de bord</h1>
                    <p className="text-sm text-slate-400 mt-1">Vue d'ensemble opérationnelle en temps réel.</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                    <Link to="/tms/ot" data-testid="tms-create-ot-shortcut" className="kz-btn-primary text-sm">
                        <ClipboardList size={14} /> Gérer les OT
                    </Link>
                    <Link to="/tms/invoices" className="kz-btn-ghost text-sm">
                        <FileText size={14} /> Facturation
                    </Link>
                </div>
            </header>

            <section className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4">
                {cards.map((c) => {
                    const Wrap = c.to ? Link : "div";
                    const props = c.to ? { to: c.to } : {};
                    return (
                        <Wrap key={c.label} {...props} className="rounded-2xl border border-slate-800 bg-[#1e293b] p-5 hover:border-orange-500/40 transition group">
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-slate-400 uppercase tracking-widest">{c.label}</div>
                                <c.icon size={16} className={c.color} />
                            </div>
                            <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-white">{c.value}</div>
                            {c.to && <div className="mt-2 text-xs text-orange-300 opacity-0 group-hover:opacity-100 transition flex items-center gap-1">Ouvrir <ArrowRight size={10} /></div>}
                        </Wrap>
                    );
                })}
            </section>

            <section className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-3" data-testid="dashboard-margin">
                {[
                    { label: "Total acheté HT", value: stats ? eur(stats.purchase_total_ht) : "—", icon: ShoppingCart, testid: "dash-purchase" },
                    { label: "Total vendu HT", value: stats ? eur(stats.sale_total_ht) : "—", icon: Receipt, testid: "dash-sale" },
                    { label: "Marge totale HT", value: stats ? eur(stats.margin_total_ht) : "—", icon: PiggyBank, testid: "dash-margin", sub: stats ? `Taux de marge : ${stats.margin_rate.toLocaleString("fr-FR")} %` : "", accent: true },
                ].map((c) => (
                    <div key={c.label} className={`rounded-2xl border p-5 ${c.accent ? "border-orange-500/30 bg-orange-500/15" : "border-slate-800 bg-[#0f172a]"}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-slate-400 uppercase tracking-widest">{c.label}</div>
                            <c.icon size={16} className="text-orange-300" />
                        </div>
                        <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-white" data-testid={c.testid}>{c.value}</div>
                        {c.sub && <div className="text-xs text-slate-300 mt-1" data-testid="dash-margin-rate">{c.sub}</div>}
                    </div>
                ))}
            </section>
            {stats?.ot_missing_purchase > 0 && (
                <p className="text-xs text-amber-300 mt-2" data-testid="dash-missing-purchase">{stats.ot_missing_purchase} OT sans prix d'achat : ils sont exclus du calcul de la marge.</p>
            )}
            <MonthlyMargin monthly={stats?.monthly} />

            <section className="mt-8 grid gap-4 lg:grid-cols-4">
                {statusTiles.map((s) => (
                    <div key={s.k} className="rounded-2xl border border-slate-800 bg-[#0f172a] p-5">
                        <div className="flex items-center justify-between">
                            <span className={`chip ${s.cls}`}><s.icon size={12} /> {s.k}</span>
                            <span className="text-xs text-slate-500">OT</span>
                        </div>
                        <div className="mt-3 text-3xl font-display font-bold text-white">
                            {stats?.ot_by_status?.[s.k] ?? "—"}
                        </div>
                    </div>
                ))}
            </section>

            <section className="mt-10">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-lg font-semibold text-white">OT récents</h2>
                    <Link to="/tms/ot" className="text-xs text-orange-300 hover:text-orange-200">Tout afficher →</Link>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-[#0f172a] overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 font-medium">Référence</th>
                                <th className="text-left px-5 py-3 font-medium">Client</th>
                                <th className="text-left px-5 py-3 font-medium">Trajet</th>
                                <th className="text-left px-5 py-3 font-medium">Statut</th>
                                <th className="text-right px-5 py-3 font-medium">Prix HT</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {recentOt.map((o) => (
                                <tr key={o.id} className="hover:bg-slate-800/40">
                                    <td className="px-5 py-3 font-mono text-slate-200">
                                        <Link to={`/tms/ot/${o.id}`} className="hover:text-orange-300">{o.reference}</Link>
                                    </td>
                                    <td className="px-5 py-3 text-slate-200">{o.client_name}</td>
                                    <td className="px-5 py-3 text-slate-400">{o.pickup_city} → {o.delivery_city}</td>
                                    <td className="px-5 py-3"><span className={`chip ${STATUS_CLS[o.status]}`}>{o.status}</span></td>
                                    <td className="px-5 py-3 text-right text-slate-200 font-mono">{(o.price_ht || 0).toLocaleString("fr-FR")} €</td>
                                </tr>
                            ))}
                            {recentOt.length === 0 && (
                                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">Aucun OT.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
